import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  Ingredient,
  StockAdjustment,
  StockAdjustmentReason,
} from "./inventory.type";
import { StockAdjustmentApi } from "./stockAdjustment.api";

export interface IngredientWritePayload {
  name: string;
  baseUnit: string;
  lowStockThreshold?: string;
  currentStock?: string;
  costPerUnit?: number;
}

export interface GetIngredientsParams {
  page?: number;
  limit?: number;
  lowStock?: boolean;
  name?: string;
  pagination?: boolean;
}

export const IngredientApi = createApi({
  reducerPath: "ingredientApi",
  tagTypes: ["ingredient"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    createIngredient: build.mutation<
      Ingredient,
      { restaurantId: string; data: IngredientWritePayload }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/ingredients`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ingredient"],
    }),

    getIngredients: build.query<
      PaginationResults<Ingredient>,
      { restaurantId: string; query?: GetIngredientsParams }
    >({
      query: ({ restaurantId, query }) => ({
        url: `restaurants/${restaurantId}/ingredients`,
        method: "GET",
        params: query,
      }),
      providesTags: ["ingredient"],
      transformResponse: (data: CollectionData<Ingredient>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getIngredient: build.query<
      Ingredient,
      { restaurantId: string; ingredientUuid: string }
    >({
      query: ({ restaurantId, ingredientUuid }) =>
        `restaurants/${restaurantId}/ingredients/${ingredientUuid}`,
      providesTags: ["ingredient"],
    }),

    updateIngredient: build.mutation<
      Ingredient,
      {
        restaurantId: string;
        ingredientUuid: string;
        data: Partial<IngredientWritePayload>;
      }
    >({
      query: ({ restaurantId, ingredientUuid, data }) => ({
        url: `restaurants/${restaurantId}/ingredients/${ingredientUuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["ingredient"],
    }),

    deleteIngredient: build.mutation<
      void,
      { restaurantId: string; ingredientUuid: string }
    >({
      query: ({ restaurantId, ingredientUuid }) => ({
        url: `restaurants/${restaurantId}/ingredients/${ingredientUuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ingredient"],
    }),

    adjustStock: build.mutation<
      StockAdjustment,
      {
        ingredientUuid: string;
        data: {
          reason: StockAdjustmentReason;
          quantity?: string;
          newCostPerUnit?: number;
          notes?: string;
          packaging?: string;
          packageQuantity?: string;
        };
      }
    >({
      query: ({ ingredientUuid, data }) => ({
        url: `ingredients/${ingredientUuid}/stock_adjustments`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ingredient"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            StockAdjustmentApi.util.invalidateTags(["stockAdjustment"])
          );
        } catch {
          // ignore
        }
      },
    }),
  }),
});

export const {
  useCreateIngredientMutation,
  useGetIngredientsQuery,
  useGetIngredientQuery,
  useUpdateIngredientMutation,
  useDeleteIngredientMutation,
  useAdjustStockMutation,
} = IngredientApi;
